import React from 'react';

import { APButton, APTypography } from '@shared/components';
import { useTranslate } from '@shared/hooks/use-translate';
import { env } from '@shared/utils/env';

import { AuthInitializationError } from '../../errors';

export interface IAuthInitializationErrorViewProps {
  error: AuthInitializationError;
  onRetry: () => void;
}

/**
 * Component to display authentication initialization errors
 * Shows user-friendly error messages with retry functionality
 */
export const AuthInitializationErrorView: React.FC<IAuthInitializationErrorViewProps> = ({
  error,
  onRetry,
}) => {
  const translate = useTranslate();

  // Format error details for development mode
  const getErrorDetails = (): string => {
    if (!error.originalError) return '';
    if (error.originalError instanceof Error) {
      return (
        error.stack ||
        error.originalError.stack ||
        error.originalError.message ||
        String(error.originalError)
      );
    }
    try {
      return JSON.stringify(error.originalError, null, 2);
    } catch {
      return String(error.originalError);
    }
  };

  const errorDetails = env.IS_DEVELOPMENT && error.originalError ? getErrorDetails() : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '24px',
        height: '100vh',
        width: '100%',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          textAlign: 'center',
        }}
      >
        <APTypography as="h1" size="xLarge" weight="500">
          {translate('errors.auth.init.title')}
        </APTypography>
        <APTypography size="base" color="error">
          {translate(error.message, undefined, translate('errors.auth.init.unknown'))}
        </APTypography>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '24px',
        }}
      >
        <APButton variant="primary" onClick={onRetry}>
          {translate('buttons.retry')}
        </APButton>
      </div>
      {errorDetails && (
        <details className="mt-4 text-left" style={{ maxWidth: '600px', width: '100%' }}>
          <summary className="cursor-pointer">{translate('errors.application.details')}</summary>
          <pre
            className="mt-2 p-4 bg-muted rounded text-xs overflow-auto"
            style={{
              marginTop: '8px',
              padding: '16px',
              background: 'var(--ap-surface-2)',
              borderRadius: '4px',
            }}
          >
            {errorDetails}
          </pre>
        </details>
      )}
    </div>
  );
};
